import React, { FC, useState, useMemo, useEffect, useContext } from 'react';
import DrawerForms from '@pixel/core/DrawerForms';
import Box from '@pixel/core/Box';
import FormInputLayout from '@pixel/core/FormInputLayout';
import { AutoSelect } from '@pixel/core';
import TextField from '@pixel/core/TextField';
import { useMultipleLocalizedText } from '../../../contexts/Localization/localizationHooks';
import axios from 'axios';
import OdsContext from '../OdsContext';
import { useSnackbar } from '@pixel/core/SnackbarService';
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import { ILocationDetailPayload, ILocationsData, ICountrySelect, ITimeZone, IStateSelect } from '../OdsTypes';
import CircularProgress from '@pixel/core/CircularProgress';
import { filterOutCurrentParentBU, transformDate, transformTime } from '../OdsTransform';
import Typography from '@pixel/core/Typography';
import Checkbox from '@pixel/core/Checkbox';
import FormControlLabel from "@pixel/core/FormControlLabel";
import { checkForAnyChangesInForm } from "../OdsTransform";

interface AddLocationsDrawerProps {
  addDrawerState: boolean;
  setaddDrawerState: any;
  editLocation: boolean;
  isAddSubLocation: boolean;
  addLocation: boolean;
  locDrawerData: ILocationDetailPayload;
  duplicateLocation: boolean;
  setDuplicateLocation: any;
  makeLocationActive: any;
  makeLocationInActive: any;
  deleteLocation: any;
}

const AddLocation: FC<AddLocationsDrawerProps> = ({
  addDrawerState,
  setaddDrawerState,
  addLocation,
  locDrawerData,
  editLocation,
  isAddSubLocation,
  duplicateLocation,
  setDuplicateLocation,
  makeLocationActive,
  makeLocationInActive,
  deleteLocation,
}) => {
  const {
    ODS_CANCEL_BUTTON_CAPTION,
    ODS_SAVE_BUTTON_CAPTION,
    ODS_DUPLICATE_CAPTION,
    ODS_MAKE_ACTIVE_CAPTION,
    ODS_MAKE_IN_ACTIVE_CAPTION,
    ODS_DESCRIPTION_CAPTION,
    ODS_LEAVE_FORM_CONFIRMATION_TITLE,
    ODS_LEAVE_FORM_CONFIRMATION_DESC,
    ODS_LOCATION_NAME_REQUIRED,
    ODS_LOCATION_SUBMITTED_SUCCESSFULLY,
    ODS_LOCATION_SUBMIT_FAILED,
    ODS_LOCATION_UDATED_SUCCESSFULLY,
    ODS_LOCATION_UPDATE_FAILED,
    ODS_LOCATION_DUPLICATE_NAME_ERROR,
    ODS_ACTIONS_MAKE_INACTIVE,
    ODS_LOCATION_SETTINGS_TITLE,
    ODS_LOCATION_NAME,
    ODS_LOCATION_PARENT,
    ODS_LOCATION_SELECT_PARENT,
    ODS_LOCATION_DETAILS,
    ODS_DELETE,
    ODS_LOCATION_REMOTE,
    ODS_LOCATION_OFFICE,
    ODS_LOCATION_ADDRESS,
    ODS_LOCATION_COUNTRY,
    ODS_LOCATION_ADDRESS_1,
    ODS_LOCATION_ADDRESS_2,
    ODS_LOCATION_CITY,
    ODS_LOCATION_STATE,
    ODS_LOCATION_ZIP_CODE,
    ODS_LOCATION_ADD,
    ODS_LOCATION_SELECT,
    ODS_DATE_TIME_LANGUAGE_TITLE,
    ODS_TIMEZONE,
    ODS_LANGUAGE,
    ODS_SHORT_DATE_FORMAT,
    ODS_LONG_DATE_FORMAT,
    ODS_SHORT_TIME_FORMAT,
    ODS_LONG_TIME_FORMAT,
    ODS_NUMBER_FORMAT,
  } = useMultipleLocalizedText()
  const snack = useSnackbar();
  const confirm = useConfirmation();
  const OrgElementsContext = useContext(OdsContext);

  const addLocationPayload: ILocationDetailPayload = useMemo(() => ({
    parentEId: null,
    name: null,
    city: null,
    state: null,
    country: null,
    postalCode: null,
    address: null,
    address2: null,
    description: null,
    office: false,
    remote: false,
    jobTimeZoneId: null,
    language: null,
    shortDateFormat: null,
    longDateFormat: null,
    shortTimeFormat: null,
    longTimeFormat: null,
    numberFormatLocale: null,
    parentName: null,
    active: true,
    createdByUser: window._jvselfUser.fullName,
    createdById: window._jvselfUser.eid,
    createdDate: new Date(),
  }), []);

  const overlayStyling = {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    alignItems: 'center',
    zIndex: '1',
  }

  const resetDrawerState = () => {
    setAddLocPayload({
      ...addLocPayload,
      name: null,
      city: null,
      state: null,
      country: null,
      postalCode: null,
      address: null,
      address2: null,
      description: null,
      office: false,
      remote: false,
      jobTimeZoneId: null,
      language: null,
      shortDateFormat: null,
      longDateFormat: null,
      shortTimeFormat: null,
      longTimeFormat: null,
      numberFormatLocale: null,
      parentName: null,
      parentEId: null,
    });
    setShowOfficeForm(false);
    setRemoteOffice(false);
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedTimeZone(null);
    setSelectedShortDate(null);
    setSelectedLongDate(null);
    setSelectedLongTime(null);
    setSelectedShortTime(null);
    setSelectedLanguage(null);
    setSelectedNumFormat(null);
    setaddDrawerState(false);
    setSelectedParentLoc(null);
    setDropdownsPopulated(null);
    setFormError({ name: false, country: false, city: false });
  }

  const [addLocPayload, setAddLocPayload] = useState<ILocationDetailPayload>(addLocationPayload);
  const [selectedParentLoc, setSelectedParentLoc] = useState(null);
  const [formError, setFormError] = useState({ name: false, country: false, city: false });
  const [parentLocList, setParentLocList] = useState(null);
  const [addLocInProgress, setAddLocInProgress] = useState(false);
  const [showDuplicateErrorName, setShowDuplicateErrorName] = useState(false);
  const [showOfficeForm, setShowOfficeForm] = useState(false);
  const [remoteOffice, setRemoteOffice] = useState(false);
  const [countryList, setCountryList] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState<ICountrySelect>(null);
  const [stateList, setStateList] = useState(null);
  const [selectedState, setSelectedState] = useState<IStateSelect>(null);
  const [timeZoneList, setTimeZoneList] = useState(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState<ITimeZone>(null);
  const [shortDateList, setShortDateList] = useState(null);
  const [selectedShortDate, setSelectedShortDate] = useState(null);
  const [longDateList, setLongDateList] = useState(null);
  const [selectedLongDate, setSelectedLongDate] = useState(null);
  const [shortTimeList, setShortTimeList] = useState(null);
  const [selectedShortTime, setSelectedShortTime] = useState(null);
  const [longTimeList, setLongTimeList] = useState(null);
  const [selectedLongTime, setSelectedLongTime] = useState(null);
  const [languageList, setLanguageList] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [numFormatList, setNumFormatList] = useState(null);
  const [selectedNumFormat, setSelectedNumFormat] = useState(null);
  const [selectStateVisibility, setSelectStateVisibility] = useState(null);
  const [dropdownsPopulated, setDropdownsPopulated] = useState(null);

  const [formState, setFormState] = useState<ILocationDetailPayload>(addLocPayload);
  const columnsToSkip = ['actions', 'subLocationsCount', 'zip', 'eid', 'companyId', 'createdById', 'createdDate', 'createdByUser'];
  const handleAddLocDrawer = () => {
    let onLoadDrawerData = formState;
    if (editLocation || duplicateLocation) {
      onLoadDrawerData = addLocPayload;
    }
    const isChanged = checkForAnyChangesInForm(editLocation || duplicateLocation ? locDrawerData : addLocPayload, onLoadDrawerData, columnsToSkip);
    if (!isChanged) {
      resetDrawerState();
    } else {
      confirm.open({
        dialogLabelSet: DialogLabelSet.CONFIRM,
        title: ODS_LEAVE_FORM_CONFIRMATION_TITLE,
        description: ODS_LEAVE_FORM_CONFIRMATION_DESC,
        onResolve: (reason: string) => {
          resetDrawerState();
        },
        onReject: (reason: string) => {
          console.log('callback rejected reason: ' + reason);
        }
      });
    }
  };

  const duplicateAddLocAction = () => {
    setDuplicateLocation(true);
    setAddLocPayload({
      ...addLocPayload,
      parentEId: locDrawerData.parentEId,
      name: '[Copy] ' + locDrawerData.name,
      description: locDrawerData.description,
      active: locDrawerData.active,
      city: locDrawerData.city,
      state: locDrawerData.state,
      country: locDrawerData.country,
      postalCode: locDrawerData.postalCode,
      address: locDrawerData.address,
      address2: locDrawerData.address2,
      office: locDrawerData.office,
      remote: locDrawerData.remote,
      jobTimeZoneId: locDrawerData.jobTimeZoneId,
      language: locDrawerData.language,
      shortDateFormat: locDrawerData.shortDateFormat,
      longDateFormat: locDrawerData.longDateFormat,
      shortTimeFormat: locDrawerData.shortTimeFormat,
      longTimeFormat: locDrawerData.longTimeFormat,
      numberFormatLocale: locDrawerData.numberFormatLocale,
      parentName: locDrawerData.parentName,
    });
  };

  const validateForm = () => {
    let temp: any = {};
    temp.name = addLocPayload.name === '' || addLocPayload.name === null;
    temp.country = showOfficeForm && (selectedCountry === null || selectedCountry == '' || selectedCountry == undefined);
    temp.city = showOfficeForm && !remoteOffice && addLocPayload.city === '';
    setFormError({
      ...temp
    });
    return Object.values(temp).every(x => x == '');
  }

  const resetOfficeForm = () =>  {
    setSelectedTimeZone(null);
    setSelectedShortDate(null);
    setSelectedLongDate(null);
    setSelectedLongTime(null);
    setSelectedShortTime(null);
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedLanguage(null);
    setSelectedNumFormat(null);
    setRemoteOffice(false);
    setAddLocPayload({
      ...addLocPayload,
      jobTimeZoneId:  null,
      shortDateFormat: null,
      longDateFormat: null,
      shortTimeFormat: null,
      longTimeFormat: null,
      state: null,
      country: null,
      postalCode: null,
      address: null,
      address2: null,
      office: false,
      remote: false,
      language: null,
      numberFormatLocale: null,
    });
  }

  const onAddLocSaveForm = () => {
    if (validateForm()) {
      setAddLocInProgress(true);
      try {
        if (editLocation && !duplicateLocation) {
          setAddLocPayload({ ...addLocPayload, parentEId: locDrawerData.parentEId });
          axios
            .put(window.jvUrls.admin + '/action/ods/locations/' + locDrawerData.eid, addLocPayload, {
              headers: { 'Content-Type': 'application/json' }
            })
            .then(function (response) {
              setAddLocInProgress(false);
              if ([201, 200].includes(response.status)) {
                resetDrawerState();
                snack.showSuccess(ODS_LOCATION_UDATED_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
              } else {
                setAddLocInProgress(false);
                snack.showError(ODS_LOCATION_UPDATE_FAILED, OrgElementsContext.snackBarAutoHideTime);
              }
            })
            .catch(function (error) {
              setAddLocInProgress(false);
              snack.showError(error.response.data.errorMessage, OrgElementsContext.snackBarAutoHideTime);
              if (error.response.data.errorMessage.search('name is already present') != -1) {
                setFormError({ ...formError, name: true });
                setShowDuplicateErrorName(true)
              }
            });
        } else {
          axios
            .post(window.jvUrls.admin + '/action/ods/locations', addLocPayload, {
              headers: {
                'Content-Type': 'application/json; charset=UTF-8'
              }
            })
            .then(function (response) {
              setAddLocInProgress(false);
              if ([201, 200].includes(response.status)) {
                resetDrawerState();
                snack.showSuccess(ODS_LOCATION_SUBMITTED_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
              } else {
                setAddLocInProgress(false);
                snack.showError(ODS_LOCATION_SUBMIT_FAILED, OrgElementsContext.snackBarAutoHideTime);
              }
            })
            .catch(function (error) {
              setAddLocInProgress(false);
              snack.showError(error.response.data.errorMessage, OrgElementsContext.snackBarAutoHideTime);
              if (error.response.data.errorMessage.search('name is already present') != -1) {
                setFormError({ ...formError, name: true });
                setShowDuplicateErrorName(true)
              }
            });
        }
      } catch (error) {
        setAddLocInProgress(false);
        console.log(error);
      }
    }
  }

  const getState = async () => {
    try {
      await axios
        .get(window.jvUrls.source + '/action/sys/countries/' + selectedCountry.twoLetterAcronym + '/states?ts=' + new Date().getTime())
        .then(function (response) {
          setStateList(response.data.resultList);
        })
        .catch(function (error) {
          console.log(error);
        });
    } catch (error) {
      console.log(error);
    }
  }

  const AdminOdsProperties = window.AdminOdsProperties;
  const modifyObjForDropDown = (data: any) => {
    return Object.keys(data).map((key) => {
      return {
        key:key,
        value:data[key]
      }
    })
  }

  const toggleOfficeForm = () => {
    setShowOfficeForm(!showOfficeForm)
    setAddLocPayload({ ...addLocPayload, office: !showOfficeForm });
  }

  const toggleRemoteLocation = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setRemoteOffice(!remoteOffice);
    setAddLocPayload({ ...addLocPayload, remote: !remoteOffice, state: '' });
    !remoteOffice && setFormError({ ...formError, city: false });

    if (checked) {
      stateList && setSelectedState({ key: '', value: '' });
    } else {
      stateList && setSelectedState({ key: 'Alaska', value: 'Alaska' });
    }
  }

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setShowDuplicateErrorName(false);
    setAddLocPayload({
      ...addLocPayload,
      [name]: value,
      parentEId: addLocPayload.parentEId,
    });
    setFormError({ ...formError, [name]: false });
  }

  const handleAutoSelectChange = (event: any, newValue: any) => {
    let parentEId = null;
    let parentName = null;
    setSelectedParentLoc(newValue);
    if (newValue && newValue.name && newValue.eid === '') {
      parentEId = null;
      parentName = null;
    } else if (newValue == null) {
      parentEId = null;
      parentName = null;
    } else {
      parentEId = newValue.eid;
      parentName = newValue.name;
    }
    setAddLocPayload({ ...addLocPayload, parentEId, parentName });
  }

  useEffect(() => {
    selectStateVisibility && getState();
    !selectStateVisibility && setAddLocPayload({ ...addLocPayload, state:null});
  }, [selectStateVisibility])

  useEffect(() => {
    if (selectedCountry && selectedCountry.twoLetterAcronym === 'US') {
        setSelectStateVisibility(true)
    } else {
        setSelectStateVisibility(false);
    }
    if(selectedCountry && addLocPayload && addLocPayload.country != selectedCountry.country) {
       addLocPayload.state = '';
    }
    if(selectedCountry == null) {
      addLocPayload.city = '';
      addLocPayload.postalCode = '';
    }
    setAddLocPayload({ ...addLocPayload,
      country: selectedCountry ? selectedCountry.country : null,
      jobTimeZoneId: selectedTimeZone ? selectedTimeZone.id : null,
      language: selectedLanguage ? selectedLanguage.key : null,
      shortDateFormat: selectedShortDate ? selectedShortDate.split(' - ')[1].replace(/[()]/g, "") : null,
      longDateFormat: selectedLongDate ? selectedLongDate.split(' - ')[1].replace(/[()]/g, "") : null,
      shortTimeFormat: selectedShortTime ? selectedShortTime.split(' - ')[1].replace(/[()]/g, "") : null,
      longTimeFormat: selectedLongTime ? selectedLongTime.split(' - ')[1].replace(/[()]/g, "") : null,
      numberFormatLocale: selectedNumFormat ? selectedNumFormat.key : null,
    });
  }, [
    selectedCountry,
    selectedLanguage,
    selectedTimeZone,
    selectedShortDate,
    selectedLongDate,
    selectedLongTime,
    selectedShortTime,
    selectedNumFormat,
  ])

  const findInArray = (list: any, listProp: string, itemProp:string) => {
    if(list !== null && locDrawerData[itemProp] !== null){
      return list.find((item: any) => item[listProp] === locDrawerData[itemProp])
    } else {
      return null;
    }
  };

  const urls = [
    window.jvUrls.admin + '/action/ods/locations/parentLocations',
    window.jvUrls.admin + '/action/myAccount/additionalInfo?ts=' + new Date().getTime(),
    window.jvUrls.source + '/action/sys/countries?ts=' + new Date().getTime(),
  ];

  const fetchparentLocList = async () => {
    try {
      const requests = urls.map((url) => axios.get(url));
      await axios.all(requests).then((responses) => {
        let parentLocResponse = responses[0].data;
        let additionalResponse = responses[1].data;
        let countryResponse = responses[2].data;
        setCountryList(countryResponse.resultList);
        editLocation && isAddSubLocation ? setParentLocList(filterOutCurrentParentBU(parentLocResponse.data, locDrawerData.eid)) : setParentLocList(parentLocResponse);
        setTimeZoneList(additionalResponse.allTimezones);
        setLanguageList(modifyObjForDropDown(additionalResponse.emailTemplatesLanguageMap));
        setShortDateList(transformDate(additionalResponse.shortDateFormats));
        setLongDateList(transformDate(additionalResponse.longDateFormats));
        setShortTimeList(transformTime(additionalResponse.shortTimeFormats));
        setLongTimeList(transformTime(additionalResponse.longTimeFormats));
        setNumFormatList(modifyObjForDropDown(additionalResponse.numFormatMap));
        setDropdownsPopulated(true);
      });
    } catch (error) {
      console.log(error);
    }
  };

  const populateFormForEdit = () => {
    countryList && setSelectedCountry(findInArray(countryList, 'country', 'country'));
    timeZoneList && setSelectedTimeZone(findInArray(timeZoneList, 'id', 'jobTimeZoneId'));
    languageList && setSelectedLanguage(findInArray(languageList, 'key', 'language'));
    numFormatList && setSelectedNumFormat(findInArray(numFormatList, 'key', 'numberFormatLocale'));
    shortDateList && locDrawerData.shortDateFormat && setSelectedShortDate(transformDate([locDrawerData.shortDateFormat])[0]);
    longDateList && locDrawerData.longDateFormat && setSelectedLongDate(transformDate([locDrawerData.longDateFormat])[0]);
    shortTimeList && locDrawerData.shortTimeFormat && setSelectedShortTime(transformTime([locDrawerData.shortTimeFormat])[0]);
    longTimeList && locDrawerData.longTimeFormat && setSelectedLongTime(transformTime([locDrawerData.longTimeFormat])[0]);
    setAddLocPayload({
      ...addLocPayload,
      parentEId: locDrawerData.parentEId,
      name: locDrawerData.name,
      description: locDrawerData.description,
      city: locDrawerData.city,
      state: locDrawerData.state,
      country: locDrawerData.country,
      postalCode: locDrawerData.postalCode,
      address: locDrawerData.address,
      address2: locDrawerData.address2,
      office: locDrawerData.office,
      remote: locDrawerData.remote,
      jobTimeZoneId: locDrawerData.jobTimeZoneId,
      language: locDrawerData.language,
      shortDateFormat: locDrawerData.shortDateFormat,
      longDateFormat: locDrawerData.longDateFormat,
      shortTimeFormat: locDrawerData.shortTimeFormat,
      longTimeFormat: locDrawerData.longTimeFormat,
      numberFormatLocale: locDrawerData.numberFormatLocale,
      parentName: locDrawerData.parentName,
    });
    setShowOfficeForm(locDrawerData.office);
    setRemoteOffice(locDrawerData.remote);
    duplicateLocation && duplicateAddLocAction();
  };

  useEffect(() => {
    if(addDrawerState && (duplicateLocation || editLocation)){
      parentLocList && setSelectedParentLoc({ name: locDrawerData.parentName, eid: locDrawerData.parentEId });
      setAddLocPayload({ ...addLocPayload, parentEId: locDrawerData.parentEId, parentName: locDrawerData.parentName });
      setFormState({ ...addLocPayload, parentEId: locDrawerData.parentEId, parentName: locDrawerData.parentName });
    }else if (addDrawerState && !duplicateLocation && isAddSubLocation) {
      parentLocList && setSelectedParentLoc({ name: locDrawerData.name, eid: locDrawerData.eid });
      setAddLocPayload({ ...addLocPayload, parentEId: locDrawerData.eid, parentName: locDrawerData.name });
      setFormState({ ...addLocPayload, parentEId: locDrawerData.eid, parentName: locDrawerData.name });
    }
    addDrawerState && editLocation && populateFormForEdit();
  }, [dropdownsPopulated]);

  useEffect(() => {
    if(showOfficeForm && addDrawerState && dropdownsPopulated && !duplicateLocation && !editLocation){
      timeZoneList && setSelectedTimeZone(timeZoneList.find((item: any) => item.id === Number(window.locale.timeZoneID)));
      AdminOdsProperties.isDatesAvailable && shortDateList && window.locale.shortDateFormat && setSelectedShortDate(transformDate([window.locale.shortDateFormat])[0]);
      AdminOdsProperties.isDatesAvailable && longDateList && window.locale.longDateFormat && setSelectedLongDate(transformDate([window.locale.longDateFormat])[0]);
      AdminOdsProperties.isDatesAvailable && shortTimeList && window.locale.shortTimeFormat && setSelectedShortTime(transformTime([window.locale.shortTimeFormat])[0]);
      AdminOdsProperties.isDatesAvailable && longTimeList && window.locale.longTimeFormat && setSelectedLongTime(transformTime([window.locale.longTimeFormat])[0]);
      setAddLocPayload({
        ...addLocPayload,
        jobTimeZoneId: window.locale.timeZoneID,
        shortDateFormat: window.locale.shortDateFormat,
        longDateFormat: window.locale.longDateFormat,
        shortTimeFormat: window.locale.shortTimeFormat,
        longTimeFormat: window.locale.longTimeFormat,
      });
    }
    if (!showOfficeForm){
      resetOfficeForm();
    }
  }, [showOfficeForm])

  useEffect(() => {
    addDrawerState && editLocation && stateList && setSelectedState(findInArray(stateList, 'value', 'state'));
    if ((addLocPayload.state == '' || addLocPayload.state == null )&& selectedCountry && selectedCountry.twoLetterAcronym === 'US' && !remoteOffice) {
        setSelectedState({key: 'Alaska' , value:'Alaska'})
    }
  }, [stateList])

  useEffect(() => {
    selectStateVisibility && selectedState && setAddLocPayload({ ...addLocPayload, state: selectedState.value });
  }, [selectedState])

  const handleMakeLocationInactive = (locDrawerData: ILocationDetailPayload) => {
    makeLocationInActive(locDrawerData);
    resetDrawerState();
  }

  const handleMakeLocationActive = (locDrawerData: ILocationDetailPayload) => {
    makeLocationActive(locDrawerData);
    resetDrawerState();
  }

  const handleDeleteLocation = (locDrawerData: ILocationDetailPayload) => {
    deleteLocation(locDrawerData);
    resetDrawerState();
  }

  const populateActionsList = (actionList: any) => {
    const LocActions = [
      { i18Label: ODS_DUPLICATE_CAPTION, name: "duplicate" },
      { i18Label: ODS_MAKE_ACTIVE_CAPTION, name: "makeActive" },
      { i18Label: ODS_ACTIONS_MAKE_INACTIVE, name: "makeInactive" },
      { i18Label: ODS_DELETE, name: "delete" },
    ];
    const actionLinks: any = [];
    if (actionList) {
      Object.keys(actionList).forEach((key) => {
        let filteredAction = LocActions.filter((e) => e.name === key);
        if (filteredAction.length > 0 && actionList[key] !== 'hidden' && actionList[key] !== null) {
          let actionTertiaryLink = {
            type: "tertiary",
            label: filteredAction[0].i18Label,
            disabled: actionList[key] === 'grayout',
            onClick: () => {
              filteredAction[0].name === 'duplicate' && duplicateAddLocAction();
              filteredAction[0].name === 'makeActive' && handleMakeLocationActive(locDrawerData);
              filteredAction[0].name === 'makeInactive' && handleMakeLocationInactive(locDrawerData);
              filteredAction[0].name === 'delete' && handleDeleteLocation(locDrawerData);
            }
          };
          actionLinks.push(actionTertiaryLink);
        }
      });
    }
    return actionLinks;
  }


  useEffect(() => {
    addDrawerState && fetchparentLocList();
  }, [addDrawerState]);

  const DrawerPartionStyle = {
    '& .drawer-container-left-section': {
      '& > .MuiBox-root': {
        marginTop: 2,
      },
      overflow: 'auto',
      paddingRight: '24px',
      marginRight: '24px',
    },
  };

  return (
    <div>
      <DrawerForms
        labelDrawerForm={ODS_LOCATION_SETTINGS_TITLE}
        labelSaveDrawerForm={editLocation && !duplicateLocation ? 'Update' : ODS_SAVE_BUTTON_CAPTION}
        labelClose={ODS_CANCEL_BUTTON_CAPTION}
        anchor='right'
        drawerWidth='35%'
        onSaveForm={onAddLocSaveForm}
        onCancel={handleAddLocDrawer}
        open={addDrawerState}
        SelectedActionGroupProps={!addLocation && locDrawerData && !duplicateLocation && {
          id: 'drawer-from-actions',
          variant: 'standard_more',
          disableIconButton: false,
          actionLabel: 'Custom drawer actions',
          actions: populateActionsList(locDrawerData.actions),
        }}
      >
        {(addLocInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
        <Box sx={DrawerPartionStyle} className={'admin-ODS-drawer-container'}>
          <Box className={'ods-location-details'}>
            <Typography variant="h4">{ODS_LOCATION_DETAILS}</Typography>
            <FormControlLabel className='loc-show-office' control={<Checkbox onChange={toggleOfficeForm} name='isOffice' checked={showOfficeForm} disabled={locDrawerData?.subLocationsCount > 0 && editLocation} />} label={ODS_LOCATION_OFFICE} />
          </Box>
          <Box className='loc-location-details-form'>
            <FormInputLayout gridLayout='calc(50% - 6px)'>
              <Box className={'loc-name-text-field'}><TextField label={ODS_LOCATION_NAME} maxLength={200} error={formError.name} errorText={showDuplicateErrorName ? ODS_LOCATION_DUPLICATE_NAME_ERROR : ODS_LOCATION_NAME_REQUIRED} name='name' onChange={handleInputChange} value={addLocPayload.name} fullWidth required></TextField></Box>
              <Box className={'loc-parent-field'}>
                <AutoSelect
                  label={ODS_LOCATION_PARENT}
                  fullWidth
                  id="parentLocation"
                  value={selectedParentLoc}
                  options={parentLocList}
                  getOptionLabel={(option: any) => option.name}
                  onChange={handleAutoSelectChange}
                  isOptionEqualToValue={(option: any, value: any) => option.eid === value.eid}
                  placeholder={ODS_LOCATION_SELECT_PARENT}
                />
              </Box>
            </FormInputLayout>
          </Box>
          <Box className={'loc-description-field'}><TextField onChange={handleInputChange} name='description' value={addLocPayload.description} label={ODS_DESCRIPTION_CAPTION} multiline rows={6} fullWidth ></TextField></Box>
          {showOfficeForm && <Box className={'ods-location-toggle-office'}>
            <FormControlLabel className='loc-show-office' control={<Checkbox onChange={toggleRemoteLocation} name='isRemoteLocation' checked={remoteOffice}  />} label={ODS_LOCATION_REMOTE} />
            <Box className={'ods-location-address-title'}>
              <Typography variant="h4">{ODS_LOCATION_ADDRESS}</Typography>
            </Box>
            <Box className={'ods-location-address'}>
              <FormInputLayout>
                <AutoSelect
                  id="country"
                  label={ODS_LOCATION_COUNTRY}
                  value={selectedCountry}
                  options={countryList}
                  error={formError.country}
                  errorText={ODS_LOCATION_SELECT + ' ' + ODS_LOCATION_COUNTRY}
                  getOptionLabel={(option: any) => option.country}
                  onChange={(e, value: ICountrySelect) => {setSelectedCountry(value); setFormError({ ...formError, country: false })}}
                  isOptionEqualToValue={(option: any, value: any) => option.threeLetterAcronym === value.threeLetterAcronym}
                  placeholder={ODS_LOCATION_SELECT + ' ' + ODS_LOCATION_COUNTRY}
                  required
                />
                <TextField
                  onChange={handleInputChange}
                  fullWidth
                  name='address'
                  autoComplete='off'
                  value={addLocPayload.address}
                  label={ODS_LOCATION_ADDRESS_1}
                  placeholder={ODS_LOCATION_ADD + ' ' + ODS_LOCATION_ADDRESS_1} />
                <TextField
                  onChange={handleInputChange}
                  fullWidth
                  name='address2'
                  autoComplete='off'
                  value={addLocPayload.address2}
                  label={ODS_LOCATION_ADDRESS_2}
                  placeholder={ODS_LOCATION_ADD + ' ' + ODS_LOCATION_ADDRESS_2} />
              </FormInputLayout>
            </Box>
            <Box className={'ods-location-address'}>
              <FormInputLayout>
                <TextField
                  onChange={handleInputChange}
                  fullWidth
                  name='city'
                  autoComplete='off'
                  error={formError.city}
                  errorText={ODS_LOCATION_ADD + ' ' + ODS_LOCATION_CITY}
                  value={addLocPayload.city}
                  label={ODS_LOCATION_CITY}
                  required={!remoteOffice}
                  placeholder={ODS_LOCATION_ADD + ' ' + ODS_LOCATION_CITY} />
                {selectStateVisibility && !remoteOffice ? (<AutoSelect
                  id="state"
                  label={ODS_LOCATION_STATE}
                  value={selectedState}
                  options={stateList}
                  getOptionLabel={(option: any) => option.value}
                  onChange={(e, value: IStateSelect) => setSelectedState(value)}
                  isOptionEqualToValue={(option: any, value: any) => option && value && option.key === value.key}
                  placeholder={ODS_LOCATION_SELECT + ' ' + ODS_LOCATION_STATE}
                  disableClearable={!remoteOffice}
                />) : (<TextField
                  onChange={handleInputChange}
                  fullWidth
                  name='state'
                  autoComplete='off'
                  value={addLocPayload.state}
                  label={ODS_LOCATION_STATE}
                  placeholder={ODS_LOCATION_ADD + ' ' + ODS_LOCATION_STATE}/>)}
                <TextField
                  onChange={handleInputChange}
                  fullWidth
                  name='postalCode'
                  value={addLocPayload.postalCode}
                  label={ODS_LOCATION_ZIP_CODE}
                  placeholder={ODS_LOCATION_ADD + ' ' + ODS_LOCATION_ZIP_CODE} />
              </FormInputLayout>
            </Box>
            {(AdminOdsProperties.isDatesAvailable || AdminOdsProperties.isLocalesAvailable || isNumberFormatEnabled) && (
              <Box className={'ods-location-date-time-title'}>
                <Typography variant="h4">{ODS_DATE_TIME_LANGUAGE_TITLE}</Typography>
              </Box>
            )}
            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2} className={'ods-date-time-wrapper'}>
              <Box gridColumn="span 6">
                <AutoSelect
                  id="timeZone"
                  className={'ods-location-timezone'}
                  fullWidth
                  label={ODS_TIMEZONE}
                  placeholder={ODS_LOCATION_SELECT + ' ' + ODS_TIMEZONE}
                  value={selectedTimeZone}
                  options={timeZoneList}
                  getOptionLabel={(option: any) => option.name}
                  onChange={(e, value: ITimeZone) => setSelectedTimeZone(value)}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                />
              </Box>
              {AdminOdsProperties.isLocalesAvailable && (
                <Box gridColumn="span 6">
                  <AutoSelect
                    id="language"
                    className={'ods-location-language'}
                    fullWidth
                    label={ODS_LANGUAGE}
                    value={selectedLanguage}
                    options={languageList}
                    placeholder={ODS_LOCATION_SELECT + ' ' + ODS_LANGUAGE}
                    getOptionLabel={(option: any) => option.value}
                    onChange={(e, value: any) => setSelectedLanguage(value)}
                    isOptionEqualToValue={(option: any, value: any) => option.key === value.key}
                  />
                </Box>
              )}
              {AdminOdsProperties.isDatesAvailable && (
                <Box gridColumn="span 6">
                  <AutoSelect
                    id="shortDate"
                    className={'ods-location-short-date'}
                    fullWidth
                    label={ODS_SHORT_DATE_FORMAT}
                    value={selectedShortDate}
                    options={shortDateList}
                    placeholder={ODS_LOCATION_SELECT + ' ' + ODS_SHORT_DATE_FORMAT}
                    getOptionLabel={(option: any) => option.toUpperCase()}
                    onChange={(e, value: any) => setSelectedShortDate(value)}
                    isOptionEqualToValue={(option: any, value: any) => option === value}
                  />
                </Box>
              )}
              {AdminOdsProperties.isDatesAvailable && (
                <Box gridColumn="span 6">
                  <AutoSelect
                    id="longDate"
                    className={'ods-location-long-date'}
                    fullWidth
                    label={ODS_LONG_DATE_FORMAT}
                    value={selectedLongDate}
                    options={longDateList}
                    placeholder={ODS_LOCATION_SELECT + ' ' + ODS_LONG_DATE_FORMAT}
                    getOptionLabel={(option: any) => option.toUpperCase()}
                    onChange={(e, value: any) => setSelectedLongDate(value)}
                    isOptionEqualToValue={(option: any, value: any) => option === value}
                  />
                </Box>
              )}
              {AdminOdsProperties.isDatesAvailable && (
                <Box gridColumn="span 6">
                  <AutoSelect
                    id="shortTime"
                    className={'ods-location-short-time'}
                    fullWidth
                    label={ODS_SHORT_TIME_FORMAT}
                    value={selectedShortTime}
                    options={shortTimeList}
                    placeholder={ODS_LOCATION_SELECT + ' ' + ODS_SHORT_TIME_FORMAT}
                    getOptionLabel={(option: any) => option.toUpperCase()}
                    onChange={(e, value: any) => setSelectedShortTime(value)}
                    isOptionEqualToValue={(option: any, value: any) => option === value}
                  />
                </Box>
              )}
              {AdminOdsProperties.isDatesAvailable && (
                <Box gridColumn="span 6">
                  <AutoSelect
                    id="longTime"
                    className={'ods-location-long-time'}
                    fullWidth
                    label={ODS_LONG_TIME_FORMAT}
                    value={selectedLongTime}
                    options={longTimeList}
                    placeholder={ODS_LOCATION_SELECT + ' ' + ODS_LONG_TIME_FORMAT}
                    getOptionLabel={(option: any) => option.toUpperCase()}
                    onChange={(e, value: any) => setSelectedLongTime(value)}
                    isOptionEqualToValue={(option: any, value: any) => option === value}
                  />
                </Box>
              )}
              {isNumberFormatEnabled && (
                <Box gridColumn="span 6">
                  <AutoSelect
                    id="numberFormat"
                    className={'ods-location-number-format'}
                    fullWidth
                    label={ODS_NUMBER_FORMAT}
                    value={selectedNumFormat}
                    options={numFormatList}
                    placeholder={ODS_LOCATION_SELECT + ' ' + ODS_NUMBER_FORMAT}
                    getOptionLabel={(option: any) => option.value}
                    onChange={(e, value: any) => setSelectedNumFormat(value)}
                    isOptionEqualToValue={(option: any, value: any) => option.key === value.key}
                  />
                </Box>
              )}
            </Box>
          </Box>}
        </Box>
      </DrawerForms>
    </div>
  );
};

export default AddLocation;